/**
 * UdhyamFetch — Auto-fire journey component
 *
 * Behaviour:
 *  - On mount, silently calls POST /api/trigger_udhyam_fetch/ in the background.
 *  - The backend looks up the user's mobile number from IndividualDetail,
 *    calls the Perfios mobile-to-Udhyam API, stores the full JSON response in
 *    BusinessDetail.udhyam_details, and writes the first URN to
 *    BusinessDetail.udyam_number.
 *  - On success (or non-blocking failure) the component calls onDone() so
 *    JourneyPage advances to the next stage.
 *  - In builder mode it renders a placeholder card instead.
 *
 * Registry metadata:
 *   autoFire: true   → JourneyPage hides the generic Continue button
 *   selfAdvances: true → backend calls advance_to_next_stage; JourneyPage
 *                        must NOT double-advance (onDone only re-fetches state)
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import IntellendLogo from "@/components/IntellendLogo";

interface UdhyamFetchProps {
  onDone?: () => void;
  isBuilderMode?: boolean;
  /** Page ID passed by JourneyPage so the session-storage key is page-scoped. */
  pageId?: number;
}

type FetchState = "loading" | "success" | "error";

const UdhyamFetch: React.FC<UdhyamFetchProps> = ({
  onDone,
  isBuilderMode = false,
  pageId,
}) => {
  const [fetchState, setFetchState] = useState<FetchState>("loading");
  const [udyamNumbers, setUdyamNumbers] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>("");
  // In-memory guard: prevents React StrictMode double-mount within a single render cycle
  const hasFiredRef = useRef(false);
  // Session-storage key scoped to this page so reload doesn't re-fire
  const sessionKey = pageId != null ? `udhyamFetch_fired_${pageId}` : null;

  useEffect(() => {
    if (isBuilderMode) return;
    if (hasFiredRef.current) return;
    // If we already fired for this page in this browser session (e.g. user reloaded
    // after a prefill gate error), do NOT fire again — the journey stage has not
    // changed, so calling onDone() here would skip the gate the user needs to fix.
    if (sessionKey && sessionStorage.getItem(sessionKey)) {
      console.log("[UdhyamFetch] Already fired for page", pageId, "— skipping re-fire on reload.");
      return;
    }
    hasFiredRef.current = true;
    if (sessionKey) sessionStorage.setItem(sessionKey, "1");

    const fetchUdhyam = async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/trigger_udhyam_fetch/`,
          {}
        );
        const data = res.data;

        if (data?.success) {
          setUdyamNumbers(data.udyam_numbers ?? []);
          setFetchState("success");
        } else {
          // Non-blocking: log the error but still advance the journey
          console.warn("[UdhyamFetch] Perfios returned success=false:", data?.error);
          setErrorMsg(data?.error ?? "Udhyam lookup returned no results.");
          setFetchState("error");
        }
      } catch (err: any) {
        console.error("[UdhyamFetch] Network/server error:", err);
        setErrorMsg("Could not fetch Udhyam details. Continuing journey.");
        setFetchState("error");
      } finally {
        // Always advance — Udhyam fetch is best-effort; a missing result
        // should not block the borrower's journey.
        if (onDone) {
          onDone();
        }
      }
    };

    fetchUdhyam();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Builder-mode placeholder
  // -------------------------------------------------------------------------
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏭</div>
        <p className="font-semibold text-gray-500">Udhyam Fetch</p>
        <p className="text-xs mt-1">
          Auto-triggers Perfios mobile-to-Udhyam lookup in the background and
          stores the result in BusinessDetail.
        </p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Loading state (shown briefly while the API call is in-flight)
  // -------------------------------------------------------------------------
  if (fetchState === "loading") {
    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="mb-8">
            <IntellendLogo size="md" />
          </div>

          <div className="relative mb-8">
            <div className="w-16 h-16 border-4 border-intellend-green/20 border-t-intellend-green rounded-full animate-spin" />
          </div>

          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Fetching Business Registration Details
          </h1>
          <p className="text-gray-500 mb-8">
            We are looking up your Udhyam registration. This usually takes a
            few seconds.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Success state (briefly visible before onDone() advances the journey)
  // -------------------------------------------------------------------------
  if (fetchState === "success") {
    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="mb-8">
            <IntellendLogo size="md" />
          </div>

          <div className="w-16 h-16 rounded-full bg-intellend-green/10 flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-intellend-green"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Udhyam Details Found
          </h1>

          {udyamNumbers.length > 0 ? (
            <div className="w-full mt-2 space-y-2">
              {udyamNumbers.map((urn) => (
                <div
                  key={urn}
                  className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono text-gray-700"
                >
                  {urn}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm mt-1">
              No Udhyam registration numbers found for this mobile number.
            </p>
          )}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Error state (non-blocking — journey still advances via onDone in finally)
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
      <div className="flex flex-col items-center text-center max-w-sm">
        <div className="mb-8">
          <IntellendLogo size="md" />
        </div>

        <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          Udhyam Lookup Skipped
        </h1>
        <p className="text-gray-500 text-sm mb-4">
          {errorMsg || "Could not retrieve Udhyam details at this time."}
        </p>
        <p className="text-gray-400 text-xs">Continuing your application…</p>
      </div>
    </div>
  );
};

export default UdhyamFetch;
